import { useState } from 'react';

import ErrorMessage from 'components/ErrorMessage';
import Loading from 'components/Loading';
import {
  useChooseProposalOptionMutation,
  useProposalQuery,
} from 'generated/graphql';
import { popToast } from 'utilities/popToast';

import ProposalConfirmationModal from './ProposalConfirmationModal';
import ProposalOptionCard from './ProposalOptionCard';

const ProposalShow = ({
  id,
  totalNights,
}: {
  id: string;
  totalNights: number;
}) => {
  const [lineItemSelections, setLineItemSelections] = useState<
    Record<string, boolean>
  >({});
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);

  const { data, loading, error, refetch } = useProposalQuery({
    variables: {
      id,
    },
    onError: (error) => {
      popToast(error.message, { type: 'error' });
    },
    onCompleted: (data) => {
      // Initialize line item selections from the data
      const initialSelections: Record<string, boolean> = {};
      data.proposal.proposalOptions.forEach((option) => {
        option.hotels.forEach((hotel) => {
          hotel.lineItems.forEach((item) => {
            initialSelections[item.id] = item.selected;
          });
        });
      });
      setLineItemSelections(initialSelections);
    },
  });

  const [chooseProposalOptionMutation, { loading: isChoosing }] =
    useChooseProposalOptionMutation({
      onCompleted: (data) => {
        popToast(
          `Proposal ${data.chooseProposalOption.id} successfully updated!`,
        );
        setShowConfirmModal(false);
        refetch();
      },
      onError: (error) => {
        popToast(`Error submitting request: ${error.message}`, {
          type: 'error',
        });
      },
    });

  const handleLineItemToggle = (itemId: string) => {
    setLineItemSelections((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };

  const handleChooseOption = (optionId: string) => {
    setSelectedOptionId(optionId);
    setShowConfirmModal(true);
  };

  const handleConfirmSelection = () => {
    if (!selectedOptionId || isChoosing) return;

    const selectedOption = data?.proposal.proposalOptions.find(
      (option) => option.id === selectedOptionId,
    );
    if (!selectedOption) return;

    // The mutation authorizes each add-on against the selected option.
    const lineItemSelectionsArray = selectedOption.hotels.flatMap((hotel) =>
      hotel.lineItems.map((item) => ({
        lineItemId: item.id,
        selected: lineItemSelections[item.id] ?? item.selected,
      })),
    );

    chooseProposalOptionMutation({
      variables: {
        id: selectedOptionId,
        lineItemSelections: lineItemSelectionsArray,
      },
    });
  };

  if (loading) {
    return <Loading />;
  }

  if (error || !data?.proposal) {
    return <ErrorMessage errorMessage={error?.message || 'No Data'} />;
  }

  const proposalOptions = data.proposal.proposalOptions;
  const shouldShowOptions = proposalOptions.length > 0;

  if (!shouldShowOptions) {
    return null;
  }

  const preferredOptionId = data.proposal.preferredProposalOptionId;
  const hasSelectedOption = !!preferredOptionId;
  const selectedOption = selectedOptionId
    ? proposalOptions.find((opt) => opt.id === selectedOptionId) || null
    : null;

  return (
    <>
      <div className="mt-6">
        <div className="space-y-6">
          {proposalOptions.map((option, index) => (
            <ProposalOptionCard
              key={option.id}
              option={option}
              optionIndex={index}
              totalNights={totalNights}
              isPreferred={preferredOptionId === option.id}
              onChooseOption={handleChooseOption}
              showSelect={true}
              isChoosing={isChoosing}
              selectedLineItems={lineItemSelections}
              onLineItemToggle={handleLineItemToggle}
              hasSelectedOption={hasSelectedOption}
              readOnly={hasSelectedOption}
            />
          ))}
        </div>
      </div>

      <ProposalConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmSelection}
        option={selectedOption}
        selectedLineItems={lineItemSelections}
        isProcessing={isChoosing}
      />
    </>
  );
};

export default ProposalShow;
